import { Hidden } from '@mui/material'
import { omitBy } from 'lodash'
import { GetStaticProps, NextPage } from 'next'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import React, { useEffect, useState } from 'react'
import { Footer } from 'src/components/Footer/Footer'
// import { Breadcrumb } from 'src/components/global/Breadcrumb'
import MainContainer from 'src/components/global/MainContainer/MainContainer'
import { ThemeOnly } from 'src/components/global/ThemeOnly'
import { ReCaptcha } from 'src/components/ReCaptcha/ReCaptcha'
// import { SolutionContentsPagination } from 'src/components/SolutionContentsPagination/SolutionContentsPagination'
// import SkeletonLoading from 'src/components/SkeletonLoading/SkeletonLoading'
import { THEME } from 'src/config/config'
import { apolloSdk } from 'src/graphql/apolloSdk'
import {
  IAlspSolutionDataFragment,
  IEnum_Solution_Type,
  useGetRelatedSubtopicTopicsLazyQuery,
  // useRelatedContentBySolutionLazyQuery,
} from 'src/graphql/generated/hooks'
import { useDefaultLogo } from 'src/hooks/useDefaultLogo'
import { useDecodedKeyword } from 'src/hooks/useGetDecodeKeyword'
// import { useRelatedContentBySolutionLazyQuery } from 'src/graphql/generated/hooks'
import { Meta } from 'src/layout/Meta'
import { Main } from 'src/templates/Main'
import { FilterOption, FilterOptionItem, TaxonomyType } from 'src/types'
import { getContentFilters } from 'src/utils/getFilters'
import { withRetries } from 'src/utils/retryHelper'
// import {
//   getAudiencesFilter,
//   getAuthorsFilter,
//   getContentTypesFilter,
//   getDateFilter,
//   getDocumentTypesFilter,
//   getPhasesFilter,
//   getProductTypesFilter,
//   getSubjectsFilter,
// } from 'src/utils/ContentFilters/contentFilters'

type Props = {
  solution: IAlspSolutionDataFragment
  slug: string
  filters: FilterOption[]
}

const PremiumModal = dynamic(() => import('src/components/global/PremiumModal').then((mod) => mod.PremiumModal), {
  loading: () => <p className="loading-placeholder"></p>,
})
const Breadcrumb = dynamic(() => import('src/components/global/Breadcrumb').then((mod) => mod.Breadcrumb), {
  loading: () => <p className="loading-placeholder"></p>,
})
const ContentHeader = dynamic(
  () => import('src/components/ContentHeader/ContentHeader').then((mod) => mod.ContentHeader),
  {
    loading: () => <p className="loading-placeholder"></p>,
  },
)
const Sidebar = dynamic(() => import('src/components/global/Sidebar').then((mod) => mod.Sidebar), {
  loading: () => <p className="loading-placeholder"></p>,
})
const SearchField = dynamic(() => import('src/components/global/SearchField').then((mod) => mod.SearchField), {
  loading: () => <p className="loading-placeholder"></p>,
})

const SolutionContentsPagination = dynamic(
  () =>
    import('src/components/SolutionContentsPagination/SolutionContentsPagination').then(
      (mod) => mod.SolutionContentsPagination,
    ),
  {
    loading: () => <p className="loading-placeholder"></p>,
  },
)

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const SolutionContents: NextPage<Props> = ({ ...props }: Props) => {
  // const [filters, setFilter] = useState([{ title: TaxonomyType.ProductType, data: [] }, ...props.filters.slice(1)])
  const [filters, setFilter] = useState(props.filters)
  const [loadRelatedSubtopicTopics, { loading, data: relatedSubtopicTopicsData }] =
    useGetRelatedSubtopicTopicsLazyQuery()
  const router = useRouter()

  const [showModal, setShowModal] = useState(false)
  // const displayName = solution?.attributes?.displayName || ''

  let href = ''
  if (props.solution?.type === IEnum_Solution_Type.Alsp) {
    href = '/alsps/' + props.solution?.slug
  } else if (props.solution?.type === IEnum_Solution_Type.Consultancy) {
    href = '/consultants/' + props.solution?.slug
  } else if (props.solution?.type === IEnum_Solution_Type.Product) {
    href = '/vendors/' + props.solution?.slug
  }
  const displayName = props.solution?.displayName || ''
  const shortDescription = props.solution?.shortDescription || ''
  const imageUrl = useDefaultLogo(props.solution?.logo?.url)

  useEffect(() => {
    ;(async () => {
      const query = { slug: props.slug }
      loadRelatedSubtopicTopics({ variables: { query: omitBy(query, (value: any) => !value) } })
    })()
  }, [props.slug])

  useEffect(() => {
    if (loading || !relatedSubtopicTopicsData) {
      return
    }

    const sortedTopics =
      relatedSubtopicTopicsData?.relatedSubtopicTopics
        ?.slice()
        .sort((a, b) => (a?.name || '').localeCompare(b?.name || '')) || []

    if (relatedSubtopicTopicsData && filters.length > 0) {
      const updatedFilters = [...filters]
      const productTypeIndex = updatedFilters.findIndex((item) => item.title === 'product type')

      // reset filter by related SubtopicTopics
      if (productTypeIndex !== -1) {
        updatedFilters[productTypeIndex] = {
          title: TaxonomyType.ProductType,
          slug: '',
          data:
            sortedTopics?.map(
              (item) =>
                ({
                  id: item?.documentId,
                  name: item?.name,
                  slug: item?.slug,
                  isPremium: item?.isPremium,
                  children:
                    item?.subTopics
                      ?.map(
                        (subTopic): FilterOptionItem => ({
                          id: subTopic?.documentId || '',
                          name: subTopic?.name || '',
                          slug: item?.slug,
                          isPremium: subTopic?.isPremium,
                        }),
                      )
                      .filter((item): item is FilterOptionItem => !!item.id && !!item.name) || [],
                }) as FilterOptionItem,
            ) || [],
        }
      }

      // const sortedUpdatedFiltersData = updatedFilters.sort((a, b) => {
      //   return a.title.localeCompare(b.title)
      // })
      setFilter(updatedFilters)
    }
  }, [loading, relatedSubtopicTopicsData])

  const searchKeyword = useDecodedKeyword(router.query.keyword)

  const meta = (
    <Meta title={displayName} description={shortDescription} url={`/alsps/${props.slug}/`} image={imageUrl} />
  )

  return (
    <ReCaptcha>
      <Main meta={meta}>
        {showModal && <PremiumModal origin="/contents/" isOpen={showModal} onClose={() => setShowModal(false)} />}
        <MainContainer id="mainContainer">
          <div className="flex flex-col bg-clouds pb-12">
            <Breadcrumb subject={displayName ?? ''} subtitle={'Related Content'} subjectRoute={href} />
            <ContentHeader />
            <div className="flex flex-col items-center justify-center bg-clouds px-5 md:px-0">
              <ThemeOnly theme="legaltech">
                <div className="!max-w-[1100px] w-full py-2">
                  <div className="md:w-1/4 pr-10">
                    <SearchField className="mb-[23px]" />
                  </div>
                </div>
              </ThemeOnly>
            </div>
          </div>
          <hr />
          <div className="flex flex-row justify-center w-full md:pl-0 md:pr-0 pl-[20px] pr-[20px]">
            <div className="!max-w-[1100px] flex flex-row justify-between w-full mb-32">
              <Hidden mdDown>
                <div className="w-1/4 pr-10 flex flex-col">
                  {/* <SearchField className="my-8" /> */}
                  <Sidebar
                    filterOptions={filters}
                    {...props}
                    // filtersDisabled={filtersDisabled}
                    defaultSelectFilterIdx={1}
                    onClickWhenDisabled={() => setShowModal(true)}
                  />
                </div>
              </Hidden>
              <SolutionContentsPagination
                filterProps={filters}
                slug={props.slug}
                sortType="category_contents"
                // filtersDisabled={filtersDisabled}
                onClickWhenDisabled={() => setShowModal(true)}
                searchKeyword={searchKeyword}
              />
            </div>
          </div>
          <ThemeOnly theme="legaltech">
            <Footer />
          </ThemeOnly>
        </MainContainer>
      </Main>
    </ReCaptcha>
  )
}

export default SolutionContents

export const getStaticProps: GetStaticProps<Props> = async ({ params }) => {
  if (THEME === 'ilta') return { revalidate: 60, notFound: true }

  const slug = params?.slug as string
  if (!params?.slug) return { revalidate: 60, notFound: true }

  try {
    //const { solutions } = await apolloSdk.alspBySlug({ slug, includeIlta: THEME === 'ilta' })
    const { solutions } = await withRetries(() => apolloSdk.alspBySlug({ slug, includeIlta: false }))
    const solution = solutions && solutions?.length > 0 ? solutions[0] : null
    if (!solution) return { revalidate: 60, notFound: true }

    const contentFilters = {
      subjectsFilter: { includeSubjectMatters: true, includeThemes: false },
      authorsFilter: { includeAuthors: true, includeExperts: true },
    }
    const filters: FilterOption[] = await getContentFilters(contentFilters)

    return {
      props: {
        solution,
        filters,
        slug,
      },
      revalidate: 60, // Only cache successful pages
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error fetching ALSP solution contents:', error)
    return {
      revalidate: 5,
      notFound: true,
    }
  }
}

export async function getStaticPaths() {
  try {
    const { solutions } = await withRetries(() => apolloSdk.allSolutionSlugs({ start: 0, type: 'ALSP' }))
    const result = solutions || []
    const paths = result
      .filter((solution) => !!solution?.slug)
      .map((solution) => ({ params: { slug: solution!.slug } }))

    return {
      paths,
      fallback: 'blocking',
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error fetching ALSP solution slugs:', error)
    return {
      paths: [],
      fallback: 'blocking',
    }
  }
}
